import { useMutation, useQueryClient } from '@tanstack/react-query';
import { changeBookingCheckout } from '@/api/generated/sdk.gen';
import { formatMoney } from '@/lib/format';
import { bookingAutomationMessagesQuery } from '@/lib/scheduled';

/**
 * The machine-readable outcomes `POST .../change-checkout` can reject with
 * (spec §4.8): `no_availability`/`stale`/`not_editable` come from the 409
 * `Problem.code`, `validation` is a 422, everything else (network, 5xx,
 * an unrecognised code) is `other`. The panel branches on this — it does not
 * string-match `detail`/`title`, which changes wording independently.
 */
export type ChangeCheckoutError = 'no_availability' | 'stale' | 'not_editable' | 'validation' | 'other';

export function changeCheckoutErrorCode(err: unknown): ChangeCheckoutError {
  const o = typeof err === 'object' && err !== null ? (err as Record<string, unknown>) : {};
  const code = typeof o['code'] === 'string' ? o['code'] : '';
  if (code === 'no_availability' || code === 'stale' || code === 'not_editable') return code;
  if (o['status'] === 422) return 'validation';
  return 'other';
}

export interface ChangeCheckoutInput {
  expectedVersion: number;
  checkout: string; // YYYY-MM-DD
  totalMinor: number;
  allowOverbook: boolean;
}

/**
 * Mutation for `POST /bookings/{id}/change-checkout` (spec §4.8, ADR-034/042
 * territory: still one transaction on the server — booking row + expected
 * checkout + ARI outbox — this hook only wires the wire call and the cache
 * effects). On success the 200 body IS the fresh BookingDetail (new
 * checkout/total/version), so it seeds `['booking', id]` directly instead of
 * waiting on a refetch — the card updates the instant the mutation resolves.
 * `['bookings']` (list totals/dates), `['ari','calendar']` (the nights just
 * changed) and the payment-schedule key (received is unaffected, but the
 * outstanding/expected split against the new total is) are invalidated
 * because their responses are not returned inline.
 */
export function useChangeCheckout(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: ChangeCheckoutInput) => {
      const res = await changeBookingCheckout({
        throwOnError: true,
        path: { id: bookingId },
        body: {
          expected_version: v.expectedVersion,
          checkout_date: v.checkout,
          total_amount_minor: v.totalMinor,
          allow_overbook: v.allowOverbook ? true : undefined,
        },
      });
      return res.data;
    },
    onSuccess: (detail) => {
      qc.setQueryData(['booking', bookingId], detail);
      void qc.invalidateQueries({ queryKey: ['bookings'] });
      void qc.invalidateQueries({ queryKey: ['ari', 'calendar'] });
      void qc.invalidateQueries({ queryKey: ['booking', bookingId, 'payment-schedule'] });
      // The Debug tab's message queue: the checkout move re-anchors the
      // booking's check-out messages (reconciled by the worker). Named
      // explicitly rather than relying on the ['bookings'] prefix above.
      void qc.invalidateQueries({ queryKey: bookingAutomationMessagesQuery(bookingId).queryKey });
    },
  });
}

/**
 * "Received X · New total Y · Balance Z" (or "Overpaid Z" when the new total
 * would fall below what's already been received) — one string, one DOM text
 * node, same reasoning as `formatCardStateLine` in lib/payments.ts. Pure so
 * the wording is testable without mounting the panel.
 */
export function checkoutMoneySummary(args: {
  receivedMinor: number;
  newTotalMinor: number;
  currency: string;
}): string {
  const { receivedMinor, newTotalMinor, currency } = args;
  const balance = newTotalMinor - receivedMinor;
  const received = formatMoney(receivedMinor, currency);
  const total = formatMoney(newTotalMinor, currency);
  if (balance < 0) {
    return `Received ${received} · New total ${total} · Overpaid ${formatMoney(-balance, currency)}`;
  }
  return `Received ${received} · New total ${total} · Balance ${formatMoney(balance, currency)}`;
}
